import React, {useState, useCallback, useContext} from 'react';
import {history} from 'client/history';

import {genericMandarkRequest} from 'resources/mandark.resource';
import {List, OrderedSet} from 'immutable';
import {
  addToast,
  copyToClipboard,
  WithTooltip,
  Dropdown,
  DropdownItem,
  SortableContainerContext,
  Icon,
  Modal,
  Dialogue,
  Text,
  Button,
  ListGroupItem,
  IconButton
} from '@albert-io/atomic';
import {
  getCurrentSort,
  clearSortValue
} from 'components/PracticeView/PracticeViewToolbar/Sort/sort.utils';
import {
  CreateAssignmentContext,
  CreateAssignmentProviderStateWithFunctions
} from 'client/CreateAssignmentV2/CreateAssignmentProvider';

import Sort from 'components/PracticeView/PracticeViewToolbar/Sort/Sort.react';
import Filters from 'components/PracticeView/PracticeViewToolbar/Filters/Filters.react';
import PracticeViewToolbar from 'components/PracticeView/PracticeViewToolbar/PracticeViewToolbar.react';
import UpgradeSubjectModal from 'components/PracticeView/UpgradeSubjectModal/UpgradeSubjectModal.react';

import {getQuestionSetsQuery} from 'client/CreateAssignmentV2/utils/createAssignment.queries';
import {AssignmentModelV3} from 'resources/GeneratedModels/Assignment/AssignmentModel.v3';
import {QuestionSetModelV1} from 'resources/augmented/QuestionSet/QuestionSetModel.v1';
import RenameModal from 'client/Folders/RenameFolderModal/RenameFolderModal.react';

import './teacher-practice-view.scss';

type Props =
  | {
      isEmpty?: false;
      assignment: AssignmentModelV3;
      isSelectAllQuestionSetsMode: boolean;
      clearQuestionSetSelections: () => void;
      toggleFoldersModal: () => void;
      onNameChange: () => void;
      toggleRearranging: () => void;
      isRearranging: boolean;
      questionSets: List<QuestionSetModelV1>;
      selectedQuestionSets: OrderedSet<string>;
      invalidateQuestionsList: () => void;
      selectedQuestionCount: number;
      resetQuestionsListOrder: () => void;
      isSorting: boolean;
      isFiltering: boolean;
      isLoading: boolean;
    }
  | {
      isEmpty: true;
    };

const TeacherAssignmentToolbar = (props: Props) => {
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showCancelRearrangeModal, setShowCancelRearrangeModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  if (props.isEmpty) {
    return (
      <PracticeViewToolbar>
        <Dropdown
          trigger={
            <Button size='s' className='u-mar-r_1' disabled>
              Assign
              <Icon icon='caret-down' className='u-mar-l_1' />
            </Button>
          }
        />
        <Sort disabled />
        <Filters active disabled={false} isLoading={false} isFolder={false} />
      </PracticeViewToolbar>
    );
  }

  const {
    assignment,
    isSelectAllQuestionSetsMode,
    clearQuestionSetSelections,
    toggleFoldersModal,
    onNameChange,
    toggleRearranging,
    isRearranging,
    questionSets = List(),
    selectedQuestionSets = OrderedSet(),
    invalidateQuestionsList,
    selectedQuestionCount,
    resetQuestionsListOrder,
    isSorting,
    isFiltering,
    isLoading
  } = props;

  const onCopySuccess = useCallback(
    () =>
      addToast({
        type: 'success',
        title: 'Link Copied!',
        message: 'The link to this assignment has been copied to your clipboard'
      }),
    []
  );

  const {bootstrapAssignment} = useContext(
    CreateAssignmentContext
  ) as CreateAssignmentProviderStateWithFunctions;
  const {setHasSorted} = useContext(SortableContainerContext);

  function handleReassign() {
    const bootstrapAssignmentObject = {
      assignmentOrigin: assignment.getName(),
      resourceType: 'assignments_v3',
      resourceId: assignment.getId()
    } as any;
    if (!isSelectAllQuestionSetsMode && selectedQuestionSets.size) {
      bootstrapAssignmentObject.questionSets = questionSets.filter((questionSet) =>
        selectedQuestionSets.includes(questionSet.getId())
      );
    } else {
      bootstrapAssignmentObject.query = getQuestionSetsQuery(
        'assignments_v3',
        assignment.getId(),
        selectedQuestionSets.toArray()
      );
    }
    bootstrapAssignment(bootstrapAssignmentObject);
    history.pushState(null, '/create-assignment/new-assignment');
  }

  function cancelRearranging(onCancel?: () => void) {
    toggleRearranging();
    setHasSorted(false);
    setShowCancelRearrangeModal(false);
    onCancel?.();
  }

  async function saveReordering() {
    const payload = {
      data: questionSets
        .map((questionSet, index) => ({
          type: 'question_sets_v1',
          id: questionSet.getId(),
          meta: {
            position: index
          }
        }))
        .toArray()
    };
    try {
      await genericMandarkRequest(
        'post',
        {
          resourcePath: ['assignments_v3', assignment.getId(), 'relationships', 'question_sets_v1']
        },
        payload
      );
      addToast({
        type: 'success',
        title: 'Success!',
        message: 'Your changes have been saved.'
      });
      cancelRearranging();
      invalidateQuestionsList();
    } catch (e) {
      addToast({
        type: 'danger',
        title: 'Error',
        message: 'Something went wrong with saving your changes.'
      });
    }
  }

  function getModalCollection() {
    return (
      <>
        {showCancelRearrangeModal && (
          <Modal
            ariaLabel='Cancel rerranging warning modal'
            handleClose={() => setShowCancelRearrangeModal(false)}
          >
            {({modalContentStyle}) => {
              return (
                <Dialogue
                  inModal
                  handleClose={() => setShowCancelRearrangeModal(false)}
                  className={modalContentStyle}
                >
                  <Dialogue.Title align='center'>
                    <Icon icon='exclamation-triangle' />
                  </Dialogue.Title>
                  <Dialogue.Body align='center'>
                    <Text>Unsaved changes will be lost.</Text>
                  </Dialogue.Body>
                  <Dialogue.BtnGroup>
                    <Button variant='text' onClick={saveReordering}>
                      Save changes
                    </Button>
                    <Button onClick={() => cancelRearranging(resetQuestionsListOrder)}>
                      Discard changes
                    </Button>
                  </Dialogue.BtnGroup>
                </Dialogue>
              );
            }}
          </Modal>
        )}
        {showRenameModal && (
          <RenameModal
            folder={assignment}
            handleClose={() => setShowRenameModal(false)}
            onSaveSuccess={onNameChange}
            isAssignment
          />
        )}
        {showUpgradeModal && <UpgradeSubjectModal handleClose={() => setShowUpgradeModal(false)} />}
      </>
    );
  }

  if (selectedQuestionCount || isSelectAllQuestionSetsMode) {
    return (
      <PracticeViewToolbar>
        <Button size='s' className='u-mar-r_1' onClick={clearQuestionSetSelections}>
          Clear selection
        </Button>
        <Dropdown
          trigger={
            <Button size='s' className='u-mar-r_1'>
              Assign
              <Icon icon='caret-down' className='u-mar-l_1' />
            </Button>
          }
        >
          <DropdownItem as='button' onClick={handleReassign}>
            Reassign your selection ({selectedQuestionCount})
          </DropdownItem>
        </Dropdown>
        <Button
          size='s'
          color='secondary'
          className='u-mar-r_1'
          variant='text'
          onClick={toggleFoldersModal}
        >
          Add to folder
        </Button>
        {getModalCollection()}
      </PracticeViewToolbar>
    );
  }
  if (isRearranging) {
    return (
      <SortableContainerContext.Consumer>
        {(value) => {
          if (!value) {
            return null;
          }
          const {sortIndices, hasSorted, cancelSortMode} = value;
          const hasStartedSorting = !sortIndices.isEmpty();

          return (
            <PracticeViewToolbar>
              <Button
                size='s'
                color='secondary'
                variant='text'
                className='u-mar-r_1'
                onClick={
                  hasSorted
                    ? () => setShowCancelRearrangeModal(true)
                    : () => cancelRearranging(cancelSortMode)
                }
              >
                <Icon icon='times' />
              </Button>
              <Button
                size='s'
                className='u-mar-r_1'
                disabled={!hasSorted && !isSorting}
                onClick={saveReordering}
              >
                Save custom order
              </Button>
              {hasStartedSorting ? (
                <Text size='m' className='u-mar-tb_auto u-mar-l_2'>
                  Click between questions to move a question.
                </Text>
              ) : (
                <>
                  <Text size='m' className='u-mar-tb_auto u-mar-l_1'>
                    Choose a question to move by clicking
                  </Text>
                  <IconButton
                    label='example reorder icon'
                    variant='solid'
                    color='primary'
                    size='s'
                    className='folder-toolbar__example-reorder u-mar-lr_1'
                    icon={['far', 'hand-pointer']}
                  />
                  <Text size='m'>below.</Text>
                </>
              )}
              {getModalCollection()}
            </PracticeViewToolbar>
          );
        }}
      </SortableContainerContext.Consumer>
    );
  }
  return (
    <PracticeViewToolbar>
      <Dropdown
        trigger={
          <Button size='s' className='u-mar-r_1'>
            Assign
            <Icon icon='caret-down' className='u-mar-l_1' />
          </Button>
        }
      >
        <DropdownItem as='button' onClick={handleReassign}>
          Reassign all
        </DropdownItem>
      </Dropdown>
      <Sort
        active={isSorting}
        ignoredActions={['random']}
        triggerButtonText='Order by'
        additionalDropdownItems={
          <>
            <ListGroupItem as={DropdownItem} onClick={clearSortValue}>
              <Text
                bold
                emphasis={getCurrentSort() === undefined ? 'high' : 'medium'}
                color={getCurrentSort() === undefined ? 'blue' : 'black'}
              >
                Custom order
              </Text>
            </ListGroupItem>
            <WithTooltip
              placement='right'
              enabled={isFiltering}
              display='block'
              content='Clear all filters to edit.'
            >
              <ListGroupItem
                footer
                icon='pencil-alt'
                as={DropdownItem}
                onClick={!isFiltering ? toggleRearranging : () => {}}
                disabled={isFiltering}
              >
                Edit custom order
              </ListGroupItem>
            </WithTooltip>
          </>
        }
      />
      <Filters
        active={isFiltering}
        disabled={!!selectedQuestionSets.size}
        isLoading={isLoading}
        isFolder={false}
      />
      <PracticeViewToolbar.Divider />
      <Button
        onClick={() => copyToClipboard(window.location.href, onCopySuccess)}
        size='s'
        color='secondary'
        variant='text'
        className='u-white-space_nowrap u-mar-r_1'
      >
        Copy share link
      </Button>
      <Dropdown
        trigger={
          <Button size='s' color='secondary' variant='text' className='u-mar-r_1'>
            More
            <Icon icon='caret-down' className='u-mar-l_1' />
          </Button>
        }
      >
        <DropdownItem onClick={() => setShowRenameModal(true)}>Rename</DropdownItem>
      </Dropdown>
      {getModalCollection()}
    </PracticeViewToolbar>
  );
};

export default TeacherAssignmentToolbar;
